import { action, makeObservable, observable, runInAction, set } from 'mobx';
import { debounce } from 'throttle-debounce';

import { OperationAbortedError } from 'src/app/errors';
import { requireService } from 'src/packages/di';

import { equalsDeep } from '../shared/utils/equalsDeep';

import type { TAlertsPersonalSetting } from './AlertsPersonalSettings.api';
import { AlertsPersonalSettingsApi, getDefaultAlertsPersonalSettings } from './AlertsPersonalSettings.api';
import type { IAlertsPersonalPreferenceService } from './IAlertsPersonalPreferenceService';

export class AlertsPersonalPreferenceService implements IAlertsPersonalPreferenceService {
  private readonly api: AlertsPersonalSettingsApi;

  @observable preferences: TAlertsPersonalSetting;

  syncedPreferences: TAlertsPersonalSetting;

  @observable preferencesId: number | null = null;

  @observable isLoading = false;
  @observable isInitialized = false;

  abortController: AbortController | null = null;

  debounceSyncPreferences = debounce(300, this.syncPreferences);

  constructor(
    private readonly notifications = requireService('notifications'),
    private readonly auth = requireService('authService'),
  ) {
    this.api = new AlertsPersonalSettingsApi(auth);
    this.preferences = getDefaultAlertsPersonalSettings();
    this.syncedPreferences = getDefaultAlertsPersonalSettings();
    makeObservable(this);
  }

  async loadPreferences() {
    this.setLoading(true);
    try {
      const preferences = await this.api.loadPreferences();
      runInAction(() => {
        this.preferences = preferences;
        this.syncedPreferences = preferences;
      });
    } catch {
      this.notifications.showErrorMessageT('errors:failedToLoadUserPreferences');
    } finally {
      this.setLoading(false);
      this.isInitialized = true;
    }
  }

  async updatePreferences(settings: Partial<TAlertsPersonalSetting>) {
    set(this.preferences, settings);
    this.debounceSyncPreferences();
  }

  async syncPreferences() {
    if (equalsDeep(this.preferences, this.syncedPreferences)) {
      return;
    }
    if (this.isLoading && this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const cloned = { ...this.preferences };
    this.setLoading(true);
    try {
      const updatedPreferences = await this.api.updatePreferences(cloned, this.abortController);
      runInAction(() => {
        this.setLoading(false);
        this.abortController = null;
        this.preferences = updatedPreferences;
        this.syncedPreferences = updatedPreferences;
      });
    } catch (error) {
      console.error(error);
      if (error instanceof OperationAbortedError) {
        return;
      }
      this.notifications.showErrorMessageT('errors:failedToUpdateUserPreferences');
      runInAction(() => {
        this.preferences = this.syncedPreferences;
        this.setLoading(false);
      });
    }
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}
